import {
  AnchoredPanelType,
  CheckBox,
  FieldWrapper,
  FormattedString,
  FormValues,
  getOverlapCidrs,
  getValue,
  InfoBlockLayout,
  InfoBlockStatus,
  InputFormGroup,
  InputFormSidePanel,
  InputFormSidePanelComponent,
  RadioGroup,
  RadioGroupOption,
  SimpleBuilder,
} from "o4a-react";
import * as React from "react";
import { mergeStyleSets, Stack } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { vnetTroubleshooting } from "../../constants/docConstants";
import { footerHeight } from "../../constants/uiConstants";
import { AzureSubnetSummary, AzureVirtualNetworkSummary } from "../../gen/clients/mchub-azure-api-client";
import { ACCESS_TYPES } from "../../helpers/resourceHelper";
import { useFeatures } from "../../hooks/useFeatures";
import { NetworkAddressInput } from "../NetworkAddressInput/NetworkAddressInput";
import { SubnetSelect } from "../SubnetSelect/SubnetSelect";
import { VNETSelect } from "../VNETSelect/VNETSelect";

const classNames = mergeStyleSets({
  separator: { padding: "2px 8px" },
  address: { width: "40%", minWidth: "107px", marginRight: "10px" },
  addressRange: { width: "60%", minWidth: "107px" },
  actionFooter: {
    position: "fixed",
    padding: "10px 10px 0px 10px",
    bottom: `${footerHeight}px`,
    width: "100%",
    height: "44px",
  },
  buttons: {
    justifyContent: "flex-start",
    marginTop: 3,
  },
});

export interface SimpleBuildCidrProps {
  defaultValue?: Array<FormValues>;
  fieldName: string;
  groupName: string;
  onChange?: () => void;
}

export const SimpleBuildCidr = (
  { defaultValue = [], fieldName, groupName, onChange }: SimpleBuildCidrProps,
): JSX.Element => (
  <FieldWrapper
    label={Messages.labels.ipsAndOrCidrs()}
    tooltip={Messages.hints.ipsAndOrCidrs()}
    required
  >
    <SimpleBuilder
      defaultValue={defaultValue}
      fieldName={fieldName}
      minRows={1}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      validator={((values: { [key: string]: any; }[]) => {
        const errors: string[] = [];
        const isRequired = values?.every(ele => !ele?.address);
        if (!values || !values?.length || isRequired) {
          errors.push(Messages.validation.required());
        } else {
          const addresses = values.map(ele => ele?.address);
          const overlaps = getOverlapCidrs(addresses);
          // eslint-disable-next-line max-len
          overlaps.forEach(overlap => errors.push(Messages.validation.addressOverlap(overlap?.cidr1, overlap?.cidr2)));
        }
        return errors.length ? errors : undefined;
      })}
    >
      <NetworkAddressInput
        label={Messages.labels.address()}
        fieldName={Fields.Address}
        className={classNames.address}
        onChange={onChange}
        groupName={groupName}
        validation={{
          containsCidrBlock: true,
          containsIpAddress: true,
        }}
      />
    </SimpleBuilder>
  </FieldWrapper>

);

export const enum Fields {
  Address = "address",
  AddressRange = "addressRange",
  IPsCidrs = "ipsCidrs",
  AccessType = "accessType",
  VNET = "VNET",
  Subnet = "Subnet",
  RouteVNET = "routeVNET",
}

export interface AdbsEditNetworkingValues {
  whitelistedIps: Array<string> | undefined,
  vnetId: string | undefined,
  subnetId: string | undefined,
}
export interface AdbsNetworkingProps {
  targetId: string;
  adbsEditNetworkingValues: AdbsEditNetworkingValues,
  subscriptionId: string | "";
  location: string | "";
  onClose: () => void;
  onSubmit: (formValues: FormValues) => void;
  componentRef?: (ref: InputFormSidePanelComponent) => void;
}

const accessTypes = (): RadioGroupOption[] => ([
  {
    id: ACCESS_TYPES.OPEN,
    text: Messages.labels.accessFromEverywhere(),
  },
  {
    id: ACCESS_TYPES.RESTRICTED,
    text: Messages.labels.accessFromAllowedIPs(),
  },
] as RadioGroupOption[]);

const accessTypeHints = (accessFromEverywhere: JSX.Element, accessFromAllowedIPs: JSX.Element): JSX.Element => (
  <>
    {accessFromEverywhere}
    <br />
    {accessFromAllowedIPs}
  </>
);

export const AdbsNetworkingEditPanel = ({
  targetId,
  adbsEditNetworkingValues,
  subscriptionId,
  location,
  componentRef,
  onSubmit,
  onClose,
}: AdbsNetworkingProps): JSX.Element => {
  const { enableAdbsPE } = useFeatures();
  const [formRef, setFormRef] = React.useState<InputFormSidePanelComponent>();
  const [defaultIpCidrs, setDefaultIpCidrs] = React.useState<{ [Fields.Address]: string }[]>([]);
  const [showIPsCidrs, setShowIPsCidrs] = React.useState<boolean>(false);
  const [showVnetAndSubNet, setShowVnetAndSubNet] = React.useState<boolean>(false);
  const [showRouteVNET, setShowRouteVNET] = React.useState<boolean>(true);
  const [isRouteVNET, setIsRouteVNET] = React.useState<boolean>(true);
  const [selectedVnet, setSelectedVnet] = React.useState<string>();
  const [selectedSubnet, setSelectedSubnet] = React.useState<string>();
  const accessOptions = accessTypes();
  const accessFromEverywhere = FormattedString({ inputText: Messages.hints.accessFromEverywhere() });
  const accessFromAllowedIPs = FormattedString(
    { inputText: Messages.hints.accessFromAllowedIPs() },
  );
  const vnetTooltip = FormattedString(
    { inputText: Messages.hints.tooltipVNET(vnetTroubleshooting) },
  ) as unknown as string;

  const internalComponentRef = (ref: InputFormSidePanelComponent): void => {
    setFormRef(ref);
    componentRef?.(ref);
  };

  React.useEffect(() => {
    const isAccessEnabled = !!adbsEditNetworkingValues.whitelistedIps?.length;

    setShowIPsCidrs(isAccessEnabled);
    setShowRouteVNET(!isAccessEnabled);

    if (!adbsEditNetworkingValues.vnetId && !adbsEditNetworkingValues.subnetId && !isAccessEnabled) {
      setIsRouteVNET(false);
      setShowVnetAndSubNet(false);
    } else if (!isAccessEnabled) {
      setIsRouteVNET(true);
      setShowVnetAndSubNet(true);
    }

    const ipCidrs = adbsEditNetworkingValues.whitelistedIps?.map(ip => ({ [Fields.Address]: ip }));

    setDefaultIpCidrs(ipCidrs || []);
    setSelectedVnet(adbsEditNetworkingValues.vnetId);
    setSelectedSubnet(adbsEditNetworkingValues.subnetId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onChangeVnet = (id: string, _: AzureVirtualNetworkSummary | undefined): void => {
    setSelectedVnet(id);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onChangeSubnet = (id: string, _: AzureSubnetSummary | undefined): void => {
    setSelectedSubnet(id);
  };

  const values = formRef?.getFormValues() ?? {};

  const whitelistIps = getValue(values, Fields.IPsCidrs, InputFormGroup) as Record<string, string>[];

  const previousWhitelistIps = (whitelistIps?.filter(value => !!value[Fields.Address]) ?? [])
    .map(value => ({ [Fields.Address]: value[Fields.Address] }));

  return (
    <InputFormSidePanel
      anchorId={targetId}
      componentRef={internalComponentRef}
      title={Messages.createNewPanels.editNetworking.title()}
      type={AnchoredPanelType.CUSTOM_WIDTH}
      customWidth={775}
      onSubmit={(formValues: FormValues) => {
        onSubmit(formValues);
      }}
      onClose={() => {
        onClose();
      }}
    >
      <Stack styles={{ root: { width: "690px" } }}>
        <RadioGroup
          fieldName={Fields.AccessType}
          label={Messages.labels.accessType()}
          options={accessOptions}
          defaultValue={!showIPsCidrs ? accessOptions[0].id : accessOptions[1].id}
          statusInfo={{
            messageType: InfoBlockStatus.INFO,
            infoLayout: InfoBlockLayout.Compact,
            message: accessTypeHints(accessFromEverywhere, accessFromAllowedIPs),
          }}
          onChange={(option: string) => {
            if (option === accessOptions[0].id) {
              setShowIPsCidrs(false);
              if (isRouteVNET) {
                setShowVnetAndSubNet(true);
              }
              setShowRouteVNET(true);
            } else {
              setShowIPsCidrs(true);
              setShowVnetAndSubNet(false);
              setShowRouteVNET(false);
            }
          }}
        />
        {showRouteVNET && enableAdbsPE && (
          <CheckBox
            fieldName={Fields.RouteVNET}
            defaultValue={isRouteVNET}
            label={Messages.labels.routeVNET()}
            statusInfo={{
              messageType: InfoBlockStatus.INFO,
              infoLayout: InfoBlockLayout.Compact,
              message: Messages.hints.routeVNET(),
            }}
            onChange={(checked: boolean) => {
              setShowVnetAndSubNet(checked);
              setIsRouteVNET(checked);
            }}
          />
        )}
        {showIPsCidrs && (
          <SimpleBuildCidr
            defaultValue={previousWhitelistIps.length ? previousWhitelistIps : defaultIpCidrs}
            fieldName={Fields.IPsCidrs}
            groupName={InputFormGroup}
          />
        )}
        {showVnetAndSubNet && enableAdbsPE && (
          <>
            <VNETSelect
              required
              defaultValue={selectedVnet ? [selectedVnet] : undefined}
              fieldName={Fields.VNET}
              label={Messages.labels.virtualNetwork()}
              tooltip={vnetTooltip}
              subscriptionId={subscriptionId}
              location={location}
              onChange={onChangeVnet}
            />
            <SubnetSelect
              required
              defaultValue={selectedSubnet ? [selectedSubnet] : undefined}
              fieldName={Fields.Subnet}
              label={Messages.labels.subnets()}
              vnetId={selectedVnet}
              subscriptionId={subscriptionId}
              location={location}
              onChange={onChangeSubnet}
            />
          </>
        )}
      </Stack>
    </InputFormSidePanel>
  );
};
